<template>
    <div class="widget-time-period w-full">
        <div class="flex mb-3">
            <VcCheckbox
                :model-value="timeModel.is_calendrical"
                :true-label="1"
                :false-label="0"
                @change="timeModel.is_calendrical = $event"
            >
                <span class="main-text-color">
                    {{ t('widget.config.isCalendrical') }}
                </span>
            </VcCheckbox>
        </div>
        <div class="flex flex-col">
            <div class="period-select mb-3">
                <RedesignFormLabel
                    :label="t('widget.config.periodType')"
                    class="mb-2"
                />
                <VcSelect
                    :model-value="timeModel.period_type"
                    :options="translatedPeriodOptions"
                    :config="{ valueKey: 'value', labelKey: 'translatedLabel' }"
                    :multiple="false"
                    placeholder="Select"
                    class="w-full"
                    @update:model-value="onChangeSelect"
                />
            </div>
            <div class="flex flex-col">
                <RedesignFormLabel
                    :label="t('widget.config.periodsBack')"
                    class="mb-3"
                />
                <VcInputNumber
                    :key="minValForCheckbox"
                    v-model="periodsBackModel"
                    :step="1"
                    :min-value="minValForCheckbox"
                    :max-value="maxValue"
                    placeholder="0"
                />
            </div>

            <div
                v-if="timeModel.period_type === 'week' && timeModel.is_calendrical"
                class="mt-3"
            >
                <VcCheckbox
                    :model-value="timeModel.is_monday_first"
                    :true-label="1"
                    :false-label="0"
                    @change="timeModel.is_monday_first = $event"
                >
                    <span class="main-text-color">
                        {{ t('widget.config.fromMonday') }}
                    </span>
                </VcCheckbox>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { VcCheckbox, VcSelect, VcInputNumber } from '@voicenter-team/voicenter-ui-plus'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import { widgetTimeOptionsForPeriod } from '@/enum/widgetTimeOptions'
import { WidgetTime } from '@/types/dashboard'
import { useVModel } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
    modelValue: WidgetTime
}>()
const emit = defineEmits<{
    (e: 'update:modelValue', v: WidgetTime): void
}>()

const timeModel = useVModel(props, 'modelValue', emit)
const minValForCheckbox = ref(0)
const maxValue = ref(10)

const translatedPeriodOptions = computed(() => {
    return widgetTimeOptionsForPeriod.map(item => ({
        ...item,
        translatedLabel: t(item.label)
    }))
})

/* VcInputNumber expects number, periods_back may be number | undefined */
const periodsBackModel = computed({
    get () {
        return timeModel.value.periods_back ?? 0
    },
    set (val: number | undefined) {
        timeModel.value.periods_back = val
    }
})

function onChangeSelect (value: string) {
    const widgetTimePeriod = widgetTimeOptionsForPeriod.find(el => el.value === value)
    timeModel.value.period_type = value
    maxValue.value = widgetTimePeriod?.max || 10
    timeModel.value.periods_back = Math.min((timeModel.value.periods_back || 0), maxValue.value)
    timeModel.value.label = widgetTimePeriod?.label
}

function initData () {
    const defaultPeriodType = timeModel.value.period_type ? timeModel.value.period_type : widgetTimeOptionsForPeriod[0].value
    if (timeModel.value.periods_back === undefined) {
        timeModel.value.periods_back = timeModel.value.is_calendrical ? 0 : 1
    }
    timeModel.value.period_type = defaultPeriodType
    maxValue.value = widgetTimeOptionsForPeriod.find(el => el.value === timeModel.value.period_type)?.max || 10
    minValForCheckbox.value = timeModel.value.is_calendrical ? 0 : 1
}

watch(
    () => timeModel.value.is_calendrical,
    (val) => {
        minValForCheckbox.value = val ? 0 : 1
    }
)

onMounted(initData)
</script>
